import { Fragment, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Box,
  Divider,
  Flex,
  Grid,
  Icon,
  Icons,
  Link,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
} from '@/ui';
import { ExperienceBadge, type ExperienceBadgeType } from './ExperienceBadge';

type BadgeDescription = {
  type: ExperienceBadgeType;
  description: ReactNode;
  href?: string;
};

const ALL_BADGE_DESCRIPTIONS: BadgeDescription[] = [
  {
    type: 'made-with-magic',
    description: (
      <>
        Every calendar starts with{' '}
        <Link color="brand.500" href="http://agendahero.com/magic" isExternal>
          Agenda Hero Magic
        </Link>
        , which reads your district&apos;s official calendar and turns it into
        clean, ready-to-use dates.
      </>
    ),
    href: '/magic',
  },
  {
    type: 'human-reviewed',
    description:
      "A person on our team has checked every date on this calendar against the district's source.",
  },
];

const LinkedBadge = ({
  type,
  href,
}: {
  type: ExperienceBadgeType;
  href: string;
}) => {
  const [isHovering, setIsHovering] = useState(false);

  return (
    <Box
      as="a"
      cursor="pointer"
      display="inline-flex"
      href={href}
      rel="noopener noreferrer"
      target="_blank"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <ExperienceBadge isAnimating={isHovering} type={type} />
    </Box>
  );
};

type Props = {
  badges: ExperienceBadgeType[];
};

export const ExperienceBadgeGroup = ({ badges }: Props) => (
  <Popover placement="bottom-start" strategy="fixed">
    <PopoverTrigger>
      <Flex
        _hover={{ opacity: 0.85 }}
        as="button"
        cursor="pointer"
        gap="2"
        outline="none"
        type="button"
        wrap="wrap"
      >
        {badges.map((type) => (
          <ExperienceBadge key={type} type={type} />
        ))}
      </Flex>
    </PopoverTrigger>

    <Portal>
      <PopoverContent shadow="lg" w="md">
        <PopoverArrow />
        <PopoverCloseButton />
        <PopoverBody p="4">
          <Flex align="center" gap="2" mb="4">
            <Icon as={Icons.Info} boxSize={4} color="brand.500" />
            <Text fontWeight="bold">What do these labels mean?</Text>
          </Flex>

          <Grid
            alignItems="start"
            columnGap="3"
            rowGap="4"
            templateColumns="160px 1fr"
          >
            {ALL_BADGE_DESCRIPTIONS.map(({ type, description, href }) => (
              <Fragment key={type}>
                {href ? (
                  <LinkedBadge href={href} type={type} />
                ) : (
                  <ExperienceBadge type={type} />
                )}
                <Text color="gray.700" fontSize="sm">
                  {description}
                </Text>
              </Fragment>
            ))}
            <Text color="gray.700" fontSize="sm" gridColumn="1 / -1">
              Notice anything off? A quick tap helps us, and helps every hero
              who opens it next.
            </Text>
          </Grid>

          <Divider my="4" />

          <Text color="gray.500" fontSize="xs">
            On any calendar, anytime, you can confirm it looks good or suggest
            an edit. Thanks for keeping things accurate with us, heroes.
          </Text>
        </PopoverBody>
      </PopoverContent>
    </Portal>
  </Popover>
);
